import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import useUpdateBestAction, {
  UpdateBestActionData,
} from 'features/onboarding/hooks/useUpdateBestAction';
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';

const OnboardingEntryPointWrapper = ({ children }: PropsWithChildren) => {
  const router = useRouter();
  const { actionId } = useLocalSearchParams<{ actionId: string }>();
  const {
    updateBestActionLoading: loading,
    error,
    executeUpdateBestAction: updateBestAction,
  }: UpdateBestActionData = useUpdateBestAction();

  const [isBestActionUpdated, setIsBestActionUpdated] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const { t: translatedText } = useTranslation('onboarding', {
    keyPrefix: 'errors',
  });

  const executeUpdateBestAction = useCallback(async () => {
    try {
      const response = await updateBestAction(actionId);
      setIsBestActionUpdated(response ?? false);
      setIsInitialLoad(false);
    } catch {
      setIsBestActionUpdated(false);
      setIsInitialLoad(false);
    }
  }, [actionId, updateBestAction]);

  useEffect(() => {
    // Action is not from onboarding flow, don't run mutation
    if (actionId) {
      void executeUpdateBestAction();
    }
  }, [actionId, executeUpdateBestAction]);

  const handleClose = () => {
    if (router.canGoBack()) {
      router.dismissAll();
    }
    router.replace('/');
  };

  const handleRetryClick = () => {
    void executeUpdateBestAction();
  };

  // Action is not from onboarding flow or the best action has been updated
  if (!actionId || isBestActionUpdated) {
    return children;
  }

  if (error || loading || !isBestActionUpdated) {
    return (
      <>
        <Stack.Screen
          options={{
            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar {...props} />
            ),
          }}
        />
        <ScreenTransitionOverlay
          isLoading={loading || isInitialLoad}
          LoadingComponent={<LoadingSpinner />}
          ErrorComponent={
            error || !isBestActionUpdated ? (
              <ErrorScreen
                screenType={ErrorScreenType.Error}
                messageHeadline={translatedText('messageHeadline')}
                messageBody={translatedText('messageBody')}
                primaryButton={{
                  title: translatedText('retryButtonLabel'),
                  onPress: handleRetryClick,
                }}
                secondaryButton={{
                  title: translatedText('goToHomePageButtonLabel'),
                  onPress: handleClose,
                }}
              />
            ) : null
          }
        />
      </>
    );
  }
};

export default OnboardingEntryPointWrapper;
